import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import AppShell from "../components/AppShell";
import ApiService from "../services/ApiService";
import ApiKeysService from "../services/ApiKeysService";
import { cn } from "../components/ui/cn";

function fmtDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

export default function SettingsView() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await ApiService.getAccountMe();
      setAccount(me?.account ?? me ?? null);
      const list = await ApiKeysService.list();
      setKeys(Array.isArray(list) ? list : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/signin");
        return;
      }
      setError(err?.response?.data?.message || err?.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSignOut() {
    try {
      await ApiService.signout();
    } catch {
      // ignore
    }
    navigate("/signin");
  }

  async function handleCreate(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setSnackbar({ severity: "warning", message: "Enter a name for this key." });
      return;
    }
    setCreating(true);
    try {
      const created = await ApiKeysService.create(trimmed);
      setCreatedKey(created);
      setName("");
      setCopied(false);
      const list = await ApiKeysService.list();
      setKeys(Array.isArray(list) ? list : []);
      setSnackbar({ severity: "success", message: "API key created. Copy it now — it won’t be shown again." });
    } catch (err) {
      setSnackbar({
        severity: "error",
        message: err?.response?.data?.message || err?.message || "Failed to create key",
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id) {
    setRevokingId(id);
    try {
      await ApiKeysService.revoke(id);
      setConfirmRevokeId(null);
      const list = await ApiKeysService.list();
      setKeys(Array.isArray(list) ? list : []);
      setSnackbar({ severity: "success", message: "API key revoked." });
    } catch (err) {
      setSnackbar({
        severity: "error",
        message: err?.response?.data?.message || err?.message || "Failed to revoke key",
      });
    } finally {
      setRevokingId(null);
    }
  }

  async function copyKey() {
    if (!createdKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setSnackbar({ severity: "success", message: "Copied to clipboard." });
    } catch {
      setSnackbar({ severity: "error", message: "Could not copy. Select and copy manually." });
    }
  }

  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  return (
    <AppShell
      account={account}
      onSignOut={handleSignOut}
      title="Settings"
    >
      <div className="mx-auto max-w-3xl space-y-8">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">
            API keys
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">
            Use a long-lived key as{" "}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.8rem] text-slate-700">
              Authorization: Bearer &lt;key&gt;
            </code>{" "}
            on the Orka Analytics MCP connection. Prefer this over putting your
            password into the login tool.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-12 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : (
          <>
            {createdKey?.key && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-amber-900">
                      New key — copy it now
                    </p>
                    <p className="mt-0.5 text-xs text-amber-800">
                      This is the only time the full key is shown.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCreatedKey(null)}
                    className="rounded-lg p-1 text-amber-700 hover:bg-amber-100"
                    aria-label="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <code className="min-w-0 flex-1 break-all rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-slate-800">
                    {createdKey.key}
                  </code>
                  <button
                    type="button"
                    onClick={copyKey}
                    className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <form
              onSubmit={handleCreate}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <label
                htmlFor="api-key-name"
                className="block text-sm font-semibold text-slate-800"
              >
                Create a key
              </label>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input
                  id="api-key-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Cursor MCP"
                  maxLength={255}
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none ring-brand/30 placeholder:text-slate-400 focus:border-brand focus:ring-2"
                />
                <button
                  type="submit"
                  disabled={creating}
                  className={cn(
                    "inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white",
                    creating ? "opacity-60" : "hover:bg-indigo-700"
                  )}
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Create
                </button>
              </div>
            </form>

            <section>
              <h3 className="mb-3 text-sm font-semibold text-slate-800">
                Active keys
              </h3>
              {activeKeys.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-10 text-center">
                  <KeyRound className="h-8 w-8 text-slate-300" />
                  <p className="text-sm font-medium text-slate-600">
                    No API keys yet
                  </p>
                  <p className="max-w-sm text-xs text-slate-500">
                    Create a key above, then paste it into your MCP client as the
                    Authorization Bearer value.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  {activeKeys.map((key) => (
                    <li
                      key={key.id}
                      className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {key.name}
                        </p>
                        <p className="mt-0.5 font-mono text-xs text-slate-500">
                          {key.keyPrefix}…
                        </p>
                        <p className="mt-1 text-[0.7rem] text-slate-400">
                          Created {fmtDateTime(key.createdAt)}
                          {key.lastUsedAt
                            ? ` · Last used ${fmtDateTime(key.lastUsedAt)}`
                            : " · Never used"}
                        </p>
                      </div>
                      {confirmRevokeId === key.id ? (
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="text-xs text-slate-500">Revoke?</span>
                          <button
                            type="button"
                            disabled={revokingId === key.id}
                            onClick={() => handleRevoke(key.id)}
                            className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                          >
                            {revokingId === key.id ? "…" : "Yes"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmRevokeId(null)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmRevokeId(key.id)}
                          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Revoke
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {revokedKeys.length > 0 && (
              <section>
                <h3 className="mb-3 text-sm font-semibold text-slate-500">
                  Revoked
                </h3>
                <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-100 bg-slate-50">
                  {revokedKeys.map((key) => (
                    <li key={key.id} className="px-4 py-3 opacity-70">
                      <p className="text-sm font-medium text-slate-600">
                        {key.name}
                      </p>
                      <p className="font-mono text-xs text-slate-400">
                        {key.keyPrefix}…
                      </p>
                      <p className="mt-1 text-[0.7rem] text-slate-400">
                        Revoked {fmtDateTime(key.revokedAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>

      {snackbar && (
        <div className="fixed bottom-4 left-1/2 z-50 w-[min(420px,calc(100%-2rem))] -translate-x-1/2">
          <div
            className={cn(
              "rounded-xl border px-4 py-3 text-sm shadow-lg",
              snackbar.severity === "error" &&
                "border-red-200 bg-red-50 text-red-800",
              snackbar.severity === "warning" &&
                "border-amber-200 bg-amber-50 text-amber-900",
              snackbar.severity === "success" &&
                "border-emerald-200 bg-emerald-50 text-emerald-900"
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <p>{snackbar.message}</p>
              <button
                type="button"
                onClick={() => setSnackbar(null)}
                className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
